import { Bell, ExternalLink, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.js';
import { useEventLogStore, type AgentEvent } from '@/stores/eventLogStore.js';
import { useNavigate } from 'react-router-dom';
import { useServices } from '@/contexts/ServicesContext.js';
import { useChatTabsStore } from '@/stores/chatTabsStore.js';
import { cn } from '@/lib/utils.js';
import { useState, useMemo } from 'react';

const TYPE_COLORS: Record<string, string> = {
  success: 'text-green-500',
  error: 'text-red-500',
  warning: 'text-amber-500',
  info: 'text-blue-500',
};

function formatTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return new Date(timestamp).toLocaleDateString();
}

function NotificationItem({ event, onNavigate }: { event: AgentEvent; onNavigate: () => void }) {
  const navigate = useNavigate();
  const { sessionService } = useServices();
  const { addTab, setActiveTab, getTab } = useChatTabsStore();

  const colorClass = TYPE_COLORS[event.type] || 'text-muted-foreground';

  const handleClick = async () => {
    if (event.sessionId) {
      const tabId = `session-${event.sessionId}`;
      const existingTab = getTab(tabId);

      if (existingTab) {
        setActiveTab(tabId);
      } else {
        addTab({
          id: tabId,
          title: 'Chat',
          sessionId: event.sessionId,
          isPinned: false,
        });
      }

      await sessionService.switchSession(event.sessionId);
      navigate(`/chat/${event.sessionId}`);
      onNavigate();
    }
  };

  return (
    <div
      className={cn(
        'px-3 py-2 hover:bg-muted/50 transition-colors',
        event.sessionId && 'cursor-pointer',
        !event.read && 'bg-primary/5'
      )}
      onClick={event.sessionId ? handleClick : undefined}
    >
      <div className="flex items-start gap-2">
        <Calendar className={cn('h-4 w-4 mt-0.5 flex-shrink-0', colorClass)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-foreground truncate">
              {event.title}
            </p>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatTimeAgo(event.timestamp)}
            </span>
          </div>
          {event.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {event.description}
            </p>
          )}
          {event.sessionId && (
            <div className="flex items-center gap-1 mt-1 text-[10px] text-primary">
              <ExternalLink className="h-3 w-3" />
              <span>View session</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function NotificationsDropdown() {
  const [open, setOpen] = useState(false);
  const events = useEventLogStore((state) => state.events);
  const markCategoryAsRead = useEventLogStore((state) => state.markCategoryAsRead);
  const clearEvents = useEventLogStore((state) => state.clearEvents);

  // Filter to only scheduler-related events
  const schedulerEvents = useMemo(() => {
    return events.filter((e) => e.category === 'scheduler');
  }, [events]);

  const unreadSchedulerCount = useMemo(() => {
    return schedulerEvents.filter((e) => !e.read).length;
  }, [schedulerEvents]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    // Mark scheduler events as read when opening the dropdown
    if (isOpen && unreadSchedulerCount > 0) {
      markCategoryAsRead('scheduler');
    }
  };

  const recentEvents = schedulerEvents.slice(0, 20);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative rounded text-muted-foreground hover:text-foreground"
            >
              <Bell className="h-4 w-4" />
              {unreadSchedulerCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]"
                >
                  {unreadSchedulerCount > 9 ? '9+' : unreadSchedulerCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">Notifications</TooltipContent>
      </Tooltip>

      <PopoverContent
        align="start"
        side="right"
        className="w-80 p-0 max-h-[400px] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <h3 className="text-sm font-semibold">Routine Activity</h3>
          {schedulerEvents.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto py-1 px-2 text-xs text-muted-foreground"
              onClick={clearEvents}
            >
              Clear all
            </Button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {recentEvents.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              No routine activity yet
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {recentEvents.map((event) => (
                <NotificationItem
                  key={event.id}
                  event={event}
                  onNavigate={() => setOpen(false)}
                />
              ))}
            </div>
          )}
        </div>

        {schedulerEvents.length > 20 && (
          <div className="px-3 py-2 border-t text-center">
            <span className="text-xs text-muted-foreground">
              Showing 20 of {schedulerEvents.length} events
            </span>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
